import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Button,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  useToast,
  Spinner,
  Text,
  Divider
} from "@chakra-ui/react";
import { CalendarIcon, RefreshIcon } from "../../utils/icons";

const PeriodStatsTab = ({ userLevel }) => {
  const [periodStats, setPeriodStats] = useState({
    period: '',
    periodType: '',
    stats: {
      total: 0,
      confirmed: 0,
      pending: 0,
      rejected: 0,
      generated: 0
    },
    byAlgorithm: [],
    byCamera: [],
    dailyStats: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [loadingPeriodStats, setLoadingPeriodStats] = useState(false);
  const toast = useToast();
  
  const fetchPeriodStats = async (period = 'today') => {
    setLoadingPeriodStats(true);
    try {
      const token = localStorage.getItem('token');
      let url = '/api/infractions/stats-by-period';
      
      if (period !== 'all') {
        url += `?period=${period}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar estatísticas por período');

      const data = await response.json();
      setPeriodStats(data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas por período:', error);
      toast({
        title: "Erro ao carregar estatísticas",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPeriodStats(false);
    }
  };

  useEffect(() => {
    // Fazer requisições para usuários de nível 2 ou superior
    if (userLevel >= 2) {
      fetchPeriodStats(selectedPeriod);
    }
  }, [selectedPeriod, userLevel]);

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  // Verificar se o usuário tem permissão para ver estatísticas
  if (userLevel < 2) {
    return (
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Flex justify="center" align="center" py={10}>
          <Text color="gray.500" fontSize="lg">
            Você não tem permissão para visualizar estatísticas por período.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" display="flex" alignItems="center">
          <CalendarIcon color="blue.500" />
          Estatísticas por Período
          {periodStats.period && (
            <Text fontSize="md" color="gray.600" ml={3}>
              - {periodStats.period}
            </Text>
          )}
        </Heading>
        <Flex gap={4} align="center">
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            width="200px"
          >
            <option value="all">Total Geral</option>
            <option value="today">Hoje</option>
            <option value="yesterday">Ontem</option>
            <option value="week">Uma Semana</option>
            <option value="month">Um Mês</option>
            <option value="year">Um Ano</option>
          </Select>
          <Button
            size="sm"
            leftIcon={<RefreshIcon />}
            colorScheme="blue"
            onClick={() => fetchPeriodStats(selectedPeriod)}
            isLoading={loadingPeriodStats}
          >
            Atualizar
          </Button>
        </Flex>
      </Flex>

      {/* Mensagem informativa para usuários de nível 2 */}
      {userLevel === 2 && (
        <Box mb={4} p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
          <Text fontSize="sm" color="blue.700">
            📊 <strong>Suas Estatísticas Pessoais:</strong> Você está visualizando apenas as infrações que você analisou.
          </Text>
        </Box>
      )}

      {loadingPeriodStats ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          {/* Estatísticas de infrações geradas */}
          <Box mb={6}>
            <Heading size="md" mb={3} color="purple.600">
              Infrações Geradas
            </Heading>
            <SimpleGrid columns={[1, 3]} spacing={4} mb={3}>
              <Stat>
                <StatLabel>Total Geradas</StatLabel>
                <StatNumber color="purple.500">{periodStats.stats?.generated || 0}</StatNumber>
                <StatHelpText>Todas as infrações</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Média Diária</StatLabel>
                <StatNumber color="purple.400">
                  {periodStats.stats?.generatedDailyAvg || 
                   ((periodStats.stats?.generated || 0) / (periodStats.dailyStats?.length || 1)).toFixed(1)}
                </StatNumber>
                <StatHelpText>Infrações por dia</StatHelpText>
              </Stat>
              
              <Stat>
                <StatLabel>Taxa de Conversão</StatLabel>
                <StatNumber color="purple.300">
                  {periodStats.stats?.generated && periodStats.stats?.total > 0 
                    ? `${((periodStats.stats.total / periodStats.stats.generated) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </StatNumber>
                <StatHelpText>Geradas → Analisadas</StatHelpText>
              </Stat>
            </SimpleGrid>
          </Box>
          
          <Divider my={4} />
          
          {/* Estatísticas de infrações analisadas */}
          <Box mb={6}>
            <Heading size="md" mb={3} color="blue.600">
              Infrações Analisadas
            </Heading>
            <SimpleGrid columns={[2, 3, 4]} spacing={4} mb={3}>
              <Stat>
                <StatLabel>Total</StatLabel>
                <StatNumber color="blue.500">{periodStats.stats?.total || 0}</StatNumber>
                <StatHelpText>Infrações analisadas</StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Confirmadas</StatLabel>
                <StatNumber color="green.500">{periodStats.stats?.confirmed || 0}</StatNumber>
                <StatHelpText>
                  {periodStats.stats?.total > 0 
                    ? `${((periodStats.stats.confirmed / periodStats.stats.total) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Pendentes</StatLabel>
                <StatNumber color="yellow.500">{periodStats.stats?.pending || 0}</StatNumber>
                <StatHelpText>
                  {periodStats.stats?.total > 0 
                    ? `${((periodStats.stats.pending / periodStats.stats.total) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>Rejeitadas</StatLabel>
                <StatNumber color="red.500">{periodStats.stats?.rejected || 0}</StatNumber>
                <StatHelpText>
                  {periodStats.stats?.total > 0 
                    ? `${((periodStats.stats.rejected / periodStats.stats.total) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </Box>

          {/* Estatísticas detalhadas - apenas para usuários com nível 3+ */}
          {userLevel >= 3 && (
            <SimpleGrid columns={[1, null, 2]} spacing={6}>
              {/* Estatísticas por algoritmo */}
              {periodStats.byAlgorithm && periodStats.byAlgorithm.length > 0 && (
                <Box p={4} borderWidth="1px" borderRadius="md">
                  <Heading size="sm" mb={3} color="blue.600">
                    Por Algoritmo
                  </Heading>
                  {periodStats.byAlgorithm.map((item, index) => (
                    <Flex key={index} justify="space-between" mb={2}>
                      <Text fontSize="sm">{item.algorithmName}</Text>
                      <Text fontSize="sm" fontWeight="bold">{item.total}</Text>
                    </Flex>
                  ))}
                </Box>
              )}

              {/* Estatísticas por câmera */}
              {periodStats.byCamera && periodStats.byCamera.length > 0 && (
                <Box p={4} borderWidth="1px" borderRadius="md">
                  <Heading size="sm" mb={3} color="green.600">
                    Por Câmera
                  </Heading>
                  {periodStats.byCamera.slice(0, 10).map((item, index) => (
                    <Flex key={index} justify="space-between" mb={2}>
                      <Text fontSize="sm">{item.cameraName}</Text>
                      <Text fontSize="sm" fontWeight="bold">{item.total}</Text>
                    </Flex>
                  ))}
                  {periodStats.byCamera.length > 10 && (
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      E mais {periodStats.byCamera.length - 10} câmeras...
                    </Text>
                  )}
                </Box>
              )}
            </SimpleGrid>
          )}

          {/* Estatísticas diárias - para usuários nível 2+ e apenas para períodos mais longos */}
          {userLevel >= 2 && periodStats.dailyStats && periodStats.dailyStats.length > 1 && (
            <Box mt={6} p={4} borderWidth="1px" borderRadius="md">
              <Heading size="sm" mb={3} color="purple.600">
                Estatísticas Diárias {userLevel === 2 && "(Suas Análises)"}
              </Heading>
              <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                {periodStats.dailyStats.map((day, index) => (
                  <Box key={index} p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="bold" mb={1}>
                      {new Date(day.date).toLocaleDateString('pt-BR')}
                    </Text>
                    <Flex justify="space-between" fontSize="xs">
                      <Text>Total: {day.total}</Text>
                      <Text color="green.600">Conf: {day.confirmed}</Text>
                      <Text color="yellow.600">Pend: {day.pending}</Text>
                      <Text color="red.600">Rej: {day.rejected}</Text>
                    </Flex>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PeriodStatsTab;